import { createFileRoute, Link } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { ColDef } from 'ag-grid-community'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Price, Product, ProductStatus } from '@/api/admin-products-types'
import {
	useCreateProductBarcode,
	useDeleteProduct,
	useDeleteProductBarcode,
	useDeleteProductUnitOfMeasure
} from '@/api/api-mutations'
import { preloadGetProduct, useGetDashboardFeatures, useGetProduct, useListProductBarcodes } from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Badge, StatusBadge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Hint } from '@/components/base/hint'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { LockedIconButton, managedExternallyLabel } from '@/components/base/managed-externally'
import { Prompt, usePrompt, useTextPrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { Select } from '@/components/base/select'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { Tooltip } from '@/components/base/tooltip'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Plus } from '@/icons/plus'
import { SquareTwoStack } from '@/icons/square-two-stack'
import { Trash } from '@/icons/trash'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/_general/products/$productUuid')({
	component: () => (
		<ConditionalRouteComponent
			routePath={Route.id}
			pathsToRenderOn={['/organization', '/edit', '/prices', '/media', '/units']}
		>
			<ProductDetails />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { productUuid } }) =>
		await preloadGetProduct(context, {
			path: { productUuid }
		}),
	staticData: {
		crumb: ({ loaderData, context: { L } }) => loaderData?.title
	}
})

function ProductDetails() {
	const product = useGetProduct({ productUuid: Route.useParams().productUuid })

	if (!product.data) {
		return null
	}
	return (
		<SingleColumnPage hasOutlet>
			<ProductGeneralSection product={product.data} />
			<ProductMediaSection product={product.data} />
			<ProductOrganizationSection product={product.data} />
			<PricingSection product={product.data} />
			<UnitsOfMeasureSection product={product.data} />
			<BarcodeSection product={product.data} />
		</SingleColumnPage>
	)
}

const getProductStatusColor = (status: string) => {
	switch (status) {
		case ProductStatus.Draft:
			return 'blue'
		case ProductStatus.Published:
			return 'green'
		case ProductStatus.Archived:
			return 'grey'
		default:
			return 'grey'
	}
}

export const ProductGeneralSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()
	const prompt = usePrompt()
	const navigate = useNavigate()

	const deleteProduct = useDeleteProduct()
	const handleDelete = async () => {
		const res = await prompt({
			title: L.General.AreYouSure(),
			description: L.Product.DeleteWarning({
				title: product.title
			}),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})

		if (!res) {
			return
		}

		const result = await deleteProduct({ path: { productUuid: product.uuid } })
		if (result.isSuccess) {
			navigate({ to: '..' })
		}
	}

	const isSingleVariant = product.variants.length === 1
	const locked = managedExternallyLabel(
		product.thirdPartySource,
		L.Enum.ThirdPartySource,
		L.Product.ManagedExternally
	)
	return (
		<>
			<Container className='divide-y'>
				<ContainerHeader
					heading={product.title}
					rightSide={
						<>
							<StatusBadge color={getProductStatusColor(product.status)}>
								{L.Enum.ProductStatus(product.status.toUpperCase() as any)}
							</StatusBadge>
							{locked ? (
								<LockedIconButton label={locked}>
									<PencilSquare />
								</LockedIconButton>
							) : (
								<Link from={Route.fullPath} to='edit'>
									<IconButton>
										<PencilSquare />
									</IconButton>
								</Link>
							)}
						</>
					}
					actions={[
						{
							actions: [
								{
									label: L.General.Delete(),
									onClick: handleDelete,
									icon: <Trash />,
									disabled: !!locked,
									disabledTooltip: locked
								}
							]
						}
					]}
				/>
				<div className='flex flex-row divide-x'>
					<div className='flex flex-1 flex-col gap-y-2 divide-y p-0'>
						{!!product.subtitle && <SectionRow title={L.Product.Subtitle()} value={product.subtitle} />}
						{!!product.description && (
							<SectionRow title={L.Product.Description()} value={product.description} />
						)}
						{!!product.externalId && (
							<SectionRow title={L.Product.ExternalId()} value={product.externalId} />
						)}
						{!!product.thirdPartySource && (
							<SectionRow
								title={L.Product.ExternalSource()}
								value={L.Enum.ThirdPartySource(product.thirdPartySource)}
							/>
						)}
						{isSingleVariant && (
							<SectionRow
								title={L.Product.PricingMethodTitle()}
								value={L.Enum.PricingMethod(product.variants[0].pricingMethod)}
							/>
						)}
						{isSingleVariant && !!product.variants[0].sku && (
							<SectionRow title={L.Product.Sku()} value={product.variants[0].sku} />
						)}
					</div>
				</div>
			</Container>
		</>
	)
}

export const ProductMediaSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()

	const images = product.images ?? []
	const hasImages = images.length > 0
	const locked = managedExternallyLabel(
		product.thirdPartySource,
		L.Enum.ThirdPartySource,
		L.Product.ManagedExternally
	)

	return (
		<Container className='divide-y'>
			<ContainerHeader
				heading={L.Product.Media.Title()}
				rightSide={
					locked ? (
						<LockedIconButton label={locked}>
							<PencilSquare />
						</LockedIconButton>
					) : (
						<Link from={Route.fullPath} to='media'>
							<IconButton>
								<PencilSquare />
							</IconButton>
						</Link>
					)
				}
			/>

			{hasImages ? (
				<div className='grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-4 px-6 py-4'>
					{images.map(image =>
						locked ? (
							<div
								key={image.uuid}
								className='relative aspect-square size-full overflow-hidden rounded-[8px] shadow-elevation-card-rest'
							>
								<AssetImage asset={image.asset} className='size-full object-contain' />
							</div>
						) : (
							<Link
								key={image.uuid}
								from={Route.fullPath}
								to='media'
								className='group relative aspect-square size-full cursor-pointer overflow-hidden rounded-[8px] shadow-elevation-card-rest transition-fg hover:shadow-elevation-card-hover'
							>
								<AssetImage asset={image.asset} className='size-full object-contain' />
							</Link>
						)
					)}
				</div>
			) : product.imageUrl ? (
				<div className='grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-4 px-6 py-4'>
					{locked ? (
						<div className='relative aspect-square size-full overflow-hidden rounded-[8px] shadow-elevation-card-rest'>
							<img
								src={product.imageUrl}
								alt={`${product.title} image`}
								className='size-full object-contain'
							/>
						</div>
					) : (
						<Link
							from={Route.fullPath}
							to='media'
							className='group relative aspect-square size-full cursor-pointer overflow-hidden rounded-[8px] shadow-elevation-card-rest transition-fg hover:shadow-elevation-card-hover'
						>
							<img
								src={product.imageUrl}
								alt={`${product.title} image`}
								className='size-full object-contain'
							/>
						</Link>
					)}
				</div>
			) : (
				<div className='flex flex-col items-center gap-y-4 pb-8 pt-6'>
					<div className='flex flex-col items-center'>
						<Text size='small' leading='compact' weight='plus' className='text-ui-fg-subtle'>
							{L.Product.Media.EmptyState.Header()}
						</Text>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Product.Media.EmptyState.Description()}
						</Text>
					</div>
					{locked ? (
						<Tooltip content={locked}>
							<span className='inline-flex'>
								<Button size='small' variant='secondary' disabled className='pointer-events-none'>
									{L.Product.Media.EmptyState.Action()}
								</Button>
							</span>
						</Tooltip>
					) : (
						<Link from={Route.fullPath} to='media'>
							<Button size='small' variant='secondary'>
								{L.Product.Media.EmptyState.Action()}
							</Button>
						</Link>
					)}
				</div>
			)}
		</Container>
	)
}

type PricingRowData = {
	id: string
	unitLabel?: string
	salesChannelName: string
	priceWithTaxDisplay: string
	priceWithoutTaxDisplay: string
	taxRateDisplay: string
}

const sellingUnitLabel = (unit: { code: string }) => unit.code

// A price targets either a sales channel (override) or a sales channel group (default). These
// helpers read whichever target the price carries so pricing surfaces can render both.
const priceTargetKey = (price: Price) => price.salesChannel?.uuid ?? price.salesChannelGroup?.uuid ?? ''
const priceTargetName = (price: Price) => price.salesChannel?.name ?? price.salesChannelGroup?.name ?? ''
const isGroupPrice = (price: Price) => !price.salesChannel && !!price.salesChannelGroup

const PricingSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()
	const firstVariant = product.variants?.[0]
	const unitsEnabled = useGetDashboardFeatures().data?.unitsOfMeasureEnabled === true
	const units = firstVariant?.unitsOfMeasure ?? []
	const showUnits = unitsEnabled && units.length > 0

	const pricingData = useMemo((): PricingRowData[] => {
		if (!firstVariant) {
			return []
		}
		const pricingRows: PricingRowData[] = []

		const toRow = (unit: (typeof units)[number] | undefined, price: (typeof firstVariant.prices)[number]) => {
			const currencyCode = price.currencyCode ?? ''

			let priceWithTaxDisplay = ''
			let priceWithoutTaxDisplay = ''
			if (price.priceInfo?.variablePrice) {
				priceWithTaxDisplay = L.Product.Pricing.VariablePrice()
				priceWithoutTaxDisplay = L.Product.Pricing.VariablePrice()
			} else if (price.priceInfo) {
				// Gross/net are computed server-side (canonical `taxAmount()`), so the dashboard
				// only renders the pre-derived breakdown to keep every surface consistent.
				priceWithTaxDisplay = L.Amount.Default({ amount: price.priceInfo.taxBreakdown.gross, currencyCode })
				priceWithoutTaxDisplay = L.Amount.Default({ amount: price.priceInfo.taxBreakdown.net, currencyCode })
			}

			const taxRateName = price.priceInfo?.taxRateName
			const taxRateDisplay = taxRateName !== undefined ? String(taxRateName) : '-'
			const targetKey = priceTargetKey(price)
			return {
				id: unit ? `${unit.uuid}:${targetKey}` : targetKey,
				unitLabel: unit ? sellingUnitLabel(unit) : undefined,
				salesChannelName: priceTargetName(price),
				priceWithTaxDisplay,
				priceWithoutTaxDisplay,
				taxRateDisplay
			} satisfies PricingRowData
		}

		const appendPrices = (
			unit: (typeof units)[number] | undefined,
			prices: (typeof firstVariant.prices)[number][]
		) => {
			const groupRows: PricingRowData[] = []
			const channelRows: PricingRowData[] = []
			for (const price of prices) {
				const row = toRow(unit, price)
				if (isGroupPrice(price)) {
					groupRows.push(row)
				} else {
					channelRows.push(row)
				}
			}
			pricingRows.push(...groupRows, ...channelRows)
		}

		if (showUnits) {
			for (const unit of units) {
				const pricesByTarget = new Map<string, (typeof unit.prices)[number]>()
				const prices = unit.isBase ? [...firstVariant.prices, ...unit.prices] : unit.prices
				for (const price of prices) {
					pricesByTarget.set(priceTargetKey(price), price)
				}
				appendPrices(unit, [...pricesByTarget.values()])
			}
		} else {
			appendPrices(undefined, firstVariant.prices)
		}

		return pricingRows
	}, [firstVariant, units, showUnits, L])

	const columns = useMemo<ColDef<PricingRowData>[]>(() => {
		const defs: ColDef<PricingRowData>[] = [
			{
				headerName: L.Fields.SalesChannel(),
				field: 'salesChannelName',
				flex: 2
			},
			{
				headerName: L.Product.Pricing.PriceWithTax(),
				field: 'priceWithTaxDisplay',
				flex: 1
			},
			{
				headerName: L.Product.Pricing.PriceWithoutTax(),
				field: 'priceWithoutTaxDisplay',
				flex: 1
			},
			{
				headerName: L.Fields.TaxRate(),
				field: 'taxRateDisplay',
				flex: 1
			}
		]
		if (showUnits) {
			defs.push({
				headerName: L.Product.UnitsOfMeasure.Unit(),
				field: 'unitLabel',
				flex: 1
			})
		}
		return defs
	}, [L, showUnits])

	const locked = managedExternallyLabel(
		product.thirdPartySource,
		L.Enum.ThirdPartySource,
		L.Product.ManagedExternally
	)

	return (
		<Container className='divide-y overflow-hidden'>
			<ContainerHeader
				heading={L.Product.Pricing.Title()}
				rightSide={
					locked ? (
						<LockedIconButton label={locked}>
							<PencilSquare />
						</LockedIconButton>
					) : (
						<Link from={Route.fullPath} to='prices'>
							<IconButton>
								<PencilSquare />
							</IconButton>
						</Link>
					)
				}
			/>
			<Table<PricingRowData>
				data={pricingData}
				columnDefs={columns}
				className='z-0'
				domLayout='autoHeight'
				suppressRowTransform
				suppressCellFocus={true}
				emptyState={
					<div className='flex flex-col items-center gap-y-4 pb-8 pt-6'>
						<div className='flex flex-col items-center'>
							<Text size='small' leading='compact' weight='plus' className='text-ui-fg-subtle'>
								{L.Product.Pricing.EmptyState.Header()}
							</Text>
							<Text size='small' className='text-ui-fg-muted'>
								{L.Product.Pricing.EmptyState.Description()}
							</Text>
						</div>
						{locked ? (
							<Tooltip content={locked}>
								<span className='inline-flex'>
									<Button size='small' variant='secondary' disabled className='pointer-events-none'>
										{L.Product.Pricing.EmptyState.Action()}
									</Button>
								</span>
							</Tooltip>
						) : (
							<Link from={Route.fullPath} to='prices'>
								<Button size='small' variant='secondary'>
									{L.Product.Pricing.EmptyState.Action()}
								</Button>
							</Link>
						)}
					</div>
				}
			/>
		</Container>
	)
}

type UnitOfMeasureRowData = {
	id: string
	isBase: boolean
	code: string
	conversionFactor: number
}

// Selling units (base each + multipacks). Shown when the tenant feature is on, including a product
// that only has its base unit. BC-synced products stay read-only; native products can add packs.
const UnitsOfMeasureSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()
	const navigate = useNavigate()
	const prompt = usePrompt()
	const deleteUnit = useDeleteProductUnitOfMeasure()
	const enabled = useGetDashboardFeatures().data?.unitsOfMeasureEnabled === true
	const locked = managedExternallyLabel(
		product.thirdPartySource,
		L.Enum.ThirdPartySource,
		L.Product.ManagedExternally
	)
	const editable = product.unitsOfMeasureEditable && !locked
	const units = product.variants?.[0]?.unitsOfMeasure ?? []

	const rows = useMemo<UnitOfMeasureRowData[]>(
		() =>
			units.map(unit => ({
				id: unit.uuid,
				isBase: unit.isBase,
				code: unit.code,
				conversionFactor: unit.conversionFactor
			})),
		[units]
	)

	const columns = useMemo<ColDef<UnitOfMeasureRowData>[]>(() => {
		const defs: ColDef<UnitOfMeasureRowData>[] = [
			{
				headerName: L.Product.UnitsOfMeasure.Unit(),
				field: 'code',
				flex: 1,
				cellRenderer: ({ data }: { data?: UnitOfMeasureRowData }) => {
					if (!data) {
						return null
					}
					return (
						<div className='flex h-full items-center gap-x-2'>
							<span>{data.code}</span>
							{data.isBase && (
								<Badge size='2xsmall' color='grey'>
									{L.Product.UnitsOfMeasure.Base()}
								</Badge>
							)}
						</div>
					)
				}
			},
			{ headerName: L.Product.UnitsOfMeasure.BaseUnitsPer(), field: 'conversionFactor', flex: 1 }
		]
		if (!editable) {
			return defs
		}
		defs.push({
			resizable: false,
			width: 102,
			sortable: false,
			cellRenderer: ({ data }: { data?: UnitOfMeasureRowData }) => {
				if (!data || data.isBase) {
					return null
				}
				return (
					<div className='flex h-full w-full items-center gap-x-2'>
						<IconButton
							className='shrink-0'
							onClick={() =>
								navigate({
									from: Route.fullPath,
									to: 'units/$unitUuid',
									params: { unitUuid: data.id }
								})
							}
						>
							<PencilSquare />
						</IconButton>
						<IconButton
							className='shrink-0'
							onClick={async () => {
								const confirmed = await prompt({
									title: L.Product.UnitsOfMeasure.Delete.Title(),
									description: L.Product.UnitsOfMeasure.Delete.Description({ code: data.code }),
									confirmText: L.General.Delete(),
									cancelText: L.General.Cancel()
								})
								if (!confirmed) {
									return
								}
								const result = await deleteUnit({
									path: { productUuid: product.uuid, unitUuid: data.id }
								})
								if (!result.isSuccess) {
									toast.error(
										(result.response as { message?: string })?.message ??
											L.Error.SomethingWentWrong()
									)
								}
							}}
						>
							<Trash />
						</IconButton>
					</div>
				)
			}
		})
		return defs
	}, [L, editable, navigate, prompt, deleteUnit, product.uuid])

	if (!enabled) {
		return null
	}

	return (
		<Container className='divide-y overflow-hidden'>
			<ContainerHeader
				heading={L.Product.UnitsOfMeasure.Title()}
				rightSide={
					editable ? (
						<Link from={Route.fullPath} to='units/create'>
							<IconButton>
								<Plus />
							</IconButton>
						</Link>
					) : locked ? (
						<LockedIconButton label={locked}>
							<PencilSquare />
						</LockedIconButton>
					) : undefined
				}
			/>
			<Table<UnitOfMeasureRowData>
				data={rows}
				columnDefs={columns}
				className='z-0'
				domLayout='autoHeight'
				suppressRowTransform
				suppressCellFocus={true}
			/>
		</Container>
	)
}

type BarcodeRowData = {
	barcode: string
	unitLabel?: string
}

// Pick a bwip-js symbology from the barcode value. EAN/UPC need a valid
// checksum, so fall back to the always-renderable code128 when they don't fit.
const getBarcodeSymbology = (value: string): string => {
	if (/^\d{13}$/.test(value)) {
		return 'ean13'
	}
	if (/^\d{12}$/.test(value)) {
		return 'upca'
	}
	if (/^\d{8}$/.test(value)) {
		return 'ean8'
	}
	return 'code128'
}

function BarcodePreview({ value }: { value: string }) {
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas || !value) {
			return
		}
		const render = (bcid: string, fallback = true) => {
			import('bwip-js/browser')
				.then(({ default: bwipjs }) => {
					try {
						bwipjs.toCanvas(canvas, {
							bcid,
							text: value,
							scale: 2,
							height: 12,
							includetext: true,
							textxalign: 'center'
						})
					} catch {
						if (fallback && bcid !== 'code128') {
							render('code128', false)
						}
					}
				})
				.catch(() => {
					// Leave the preview blank if the barcode renderer fails to load.
				})
		}
		render(getBarcodeSymbology(value))
	}, [value])

	return (
		<div className='rounded bg-white p-2'>
			<canvas ref={canvasRef} />
		</div>
	)
}

export function BarcodeSection({ product }: { product: Product }) {
	const { L } = useI18n()
	const unitsEnabled = useGetDashboardFeatures().data?.unitsOfMeasureEnabled === true
	const locked = managedExternallyLabel(
		product.thirdPartySource,
		L.Enum.ThirdPartySource,
		L.Product.ManagedExternally
	)
	const units = product.variants?.[0]?.unitsOfMeasure ?? []
	const showUnits = unitsEnabled && units.length > 0
	const listedBarcodes = useListProductBarcodes({ productUuid: product.uuid }, { enabled: !showUnits })
	const [addOpen, setAddOpen] = useState(false)
	const [barcodeDraft, setBarcodeDraft] = useState('')
	const [unitUuid, setUnitUuid] = useState('')
	const [addError, setAddError] = useState<string>()
	const [addingBarcode, setAddingBarcode] = useState(false)
	const addingBarcodeRef = useRef(false)

	const barcodeRows: BarcodeRowData[] = useMemo(() => {
		if (!showUnits) {
			return listedBarcodes.data?.map(barcode => ({ barcode })) ?? []
		}
		return units.flatMap(unit =>
			unit.barcodes.map(barcode => ({
				barcode,
				unitLabel: sellingUnitLabel(unit)
			}))
		)
	}, [showUnits, listedBarcodes.data, units, L])

	const existingBarcodes = useMemo(
		() => (showUnits ? units.flatMap(unit => unit.barcodes) : (listedBarcodes.data ?? [])),
		[showUnits, units, listedBarcodes.data]
	)

	const createBarcode = useCreateProductBarcode()
	const deleteBarcode = useDeleteProductBarcode()
	const prompt = usePrompt()
	const textPrompt = useTextPrompt()

	const columns = useMemo<ColDef<BarcodeRowData>[]>(() => {
		const defs: ColDef<BarcodeRowData>[] = [
			{
				headerName: L.Product.Barcode.Barcode(),
				field: 'barcode',
				flex: 1,
				cellClass: 'select-text cursor-text',
				cellRenderer: ({ value }: { value: string }) => (
					<Tooltip content={<BarcodePreview value={value} />} side='right' maxWidth={320} className='p-0'>
						<span>{value}</span>
					</Tooltip>
				)
			}
		]
		if (showUnits) {
			defs.push({
				headerName: L.Product.UnitsOfMeasure.Unit(),
				field: 'unitLabel',
				flex: 1
			})
		}
		defs.push({
			resizable: false,
			width: 102,
			sortable: false,
			cellRenderer: ({ data }: { data?: BarcodeRowData }) => {
				if (!data) {
					return null
				}
				return (
					<div className='flex h-full w-full items-center gap-x-2'>
						<IconButton
							className='shrink-0'
							onClick={async () => {
								await navigator.clipboard.writeText(data.barcode)
								toast.success(L.General.Copied())
							}}
						>
							<SquareTwoStack className='text-ui-fg-subtle' />
						</IconButton>
						{locked ? (
							<LockedIconButton label={locked}>
								<Trash />
							</LockedIconButton>
						) : (
							<IconButton
								className='shrink-0'
								onClick={async () => {
									const res = await prompt({
										title: L.Product.Barcode.Delete.Title(),
										description: L.Product.Barcode.Delete.Description({
											barcode: data.barcode
										}),
										confirmText: L.General.Delete(),
										cancelText: L.General.Cancel()
									})
									if (!res) {
										return
									}
									await deleteBarcode({
										path: { productUuid: product.uuid, barcode: data.barcode }
									})
								}}
							>
								<Trash />
							</IconButton>
						)}
					</div>
				)
			}
		})
		return defs
	}, [L, showUnits, prompt, deleteBarcode, product.uuid, locked])

	const barcodeError = (value: string) => {
		if (value.length === 0) {
			return L.General.Required()
		}
		if (existingBarcodes.includes(value)) {
			return L.Product.Barcode.AlreadyOnProduct()
		}
		return undefined
	}

	const baseUnit = units.find(unit => unit.isBase) ?? units[0]

	return (
		<Container className='divide-y overflow-hidden'>
			<ContainerHeader
				heading={L.Product.Barcode.Title()}
				rightSide={
					locked ? (
						<LockedIconButton label={locked}>
							<Plus />
						</LockedIconButton>
					) : (
						<IconButton
							onClick={async () => {
								if (!showUnits || units.length <= 1) {
									const barcode = await textPrompt({
										title: L.Product.Barcode.AddBarcodePrompt.Title(),
										placeholder: L.Product.Barcode.AddBarcodePrompt.Placeholder(),
										confirmText: L.General.Add(),
										cancelText: L.General.Cancel(),
										isValid: barcodeError
									})
									if (!barcode) {
										return
									}
									const result = await createBarcode({
										body: { barcode },
										path: { productUuid: product.uuid }
									})
									if (!result.isSuccess) {
										toast.error(
											(result.response as { message?: string })?.message ??
												L.Product.Barcode.AlreadyInUse()
										)
									}
									return
								}
								setBarcodeDraft('')
								setUnitUuid(baseUnit?.uuid ?? '')
								setAddError(undefined)
								setAddOpen(true)
							}}
						>
							<Plus />
						</IconButton>
					)
				}
			/>
			<Table<BarcodeRowData>
				data={barcodeRows}
				columnDefs={columns}
				domLayout='autoHeight'
				suppressCellFocus={true}
			/>
			<Prompt open={addOpen} variant='confirmation' onOpenChange={open => setAddOpen(open)}>
				<Prompt.Content>
					<form
						onSubmit={async event => {
							event.preventDefault()
							if (addingBarcodeRef.current) {
								return
							}
							const formData = new FormData(event.currentTarget)
							const barcodeField = formData.get('barcode')
							const unitField = formData.get('unitOfMeasureUuid')
							const barcode = typeof barcodeField === 'string' ? barcodeField.trim() : ''
							const selectedUnit = typeof unitField === 'string' ? unitField : ''
							const error = barcodeError(barcode)
							if (error || !selectedUnit) {
								setAddError(error ?? L.General.Required())
								return
							}
							setAddError(undefined)
							addingBarcodeRef.current = true
							setAddingBarcode(true)
							const result = await createBarcode({
								body: { barcode, unitOfMeasureUuid: selectedUnit },
								path: { productUuid: product.uuid }
							})
							addingBarcodeRef.current = false
							setAddingBarcode(false)
							if (!result.isSuccess) {
								toast.error(
									(result.response as { message?: string })?.message ??
										L.Product.Barcode.AlreadyInUse()
								)
								return
							}
							setAddOpen(false)
						}}
					>
						<Prompt.Header>
							<Prompt.Title>{L.Product.Barcode.AddBarcodePrompt.Title()}</Prompt.Title>
						</Prompt.Header>
						<div className='mt-6 flex flex-col gap-y-4 border-y border-ui-border-base p-6'>
							<div className='flex flex-col gap-y-2'>
								<Label htmlFor='barcode'>{L.Product.Barcode.Barcode()}</Label>
								<Input
									id='barcode'
									name='barcode'
									autoFocus
									autoComplete='off'
									value={barcodeDraft}
									placeholder={L.Product.Barcode.AddBarcodePrompt.Placeholder()}
									onChange={event => {
										setBarcodeDraft(event.target.value)
										setAddError(undefined)
									}}
								/>
								{addError && <Hint variant='error'>{addError}</Hint>}
							</div>
							<div className='flex flex-col gap-y-2'>
								<Label htmlFor='barcode-unit'>{L.Product.UnitsOfMeasure.Unit()}</Label>
								<Select
									name='unitOfMeasureUuid'
									value={unitUuid || undefined}
									onValueChange={setUnitUuid}
								>
									<Select.Trigger id='barcode-unit'>
										<Select.Value />
									</Select.Trigger>
									<Select.Content>
										{units.map(unit => (
											<Select.Item key={unit.uuid} value={unit.uuid}>
												{sellingUnitLabel(unit)}
											</Select.Item>
										))}
									</Select.Content>
								</Select>
							</div>
						</div>
						<Prompt.Footer>
							<Prompt.Cancel type='button' onClick={() => setAddOpen(false)}>
								{L.General.Cancel()}
							</Prompt.Cancel>
							<Button type='submit' size='small' isLoading={addingBarcode} disabled={addingBarcode}>
								{L.General.Add()}
							</Button>
						</Prompt.Footer>
					</form>
				</Prompt.Content>
			</Prompt>
		</Container>
	)
}

export const ProductOrganizationSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()
	const locked = managedExternallyLabel(
		product.thirdPartySource,
		L.Enum.ThirdPartySource,
		L.Product.ManagedExternally
	)

	return (
		<Container className='divide-y p-0'>
			<ContainerHeader
				heading={L.Product.Organization.Header()}
				rightSide={
					locked ? (
						<LockedIconButton label={locked}>
							<PencilSquare />
						</LockedIconButton>
					) : (
						<Link from={Route.fullPath} to='organization'>
							<IconButton>
								<PencilSquare />
							</IconButton>
						</Link>
					)
				}
			/>
			<SectionRow
				title={L.Fields.Groups()}
				value={
					product.groups?.length
						? product.groups.map(group => (
								<OrganizationTag
									key={group.uuid}
									label={group.name}
									to={`/product-groups/${group.uuid}`}
								/>
							))
						: undefined
				}
			/>

			<SectionRow
				title={L.Fields.Categories()}
				value={
					product.categories?.length
						? product.categories.map(pcat => (
								<OrganizationTag
									key={pcat.uuid}
									label={pcat.name}
									to={`/product-categories/${pcat.uuid}`}
								/>
							))
						: undefined
				}
			/>
		</Container>
	)
}

const OrganizationTag = ({ label, to }: { label: string; to: string }) => {
	return (
		<Tooltip content={label}>
			<Badge size='2xsmall' className='block w-fit truncate' asChild>
				<Link to={to}>{label}</Link>
			</Badge>
		</Tooltip>
	)
}
